import Image from 'next/image';
import {
  type ReactElement,
  type ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { InView } from 'react-intersection-observer';

import Title from '@/components/atoms/Title';
import { SectionWrap } from '@/components/store/components/SectionWrap/SectionWrap';

import {
  ContentColumn,
  DesktopLayout,
  FeatureDescription,
  FeatureItem,
  FeatureList,
  FeaturesWrapper,
  FeatureTitle,
  ImageColumn,
  ImageWrapper,
  MobileLayout,
} from './Features.styled';

const features = [
  {
    id: 1,
    emoji: '🗺️',
    title: 'Interaktywna Mapa',
    description:
      'Znajdź szybko drogę do dowolnego miejsca: atrakcji, plaży, restauracji czy punktu widokowego, dzięki mapom, które możesz mieć cały czas pod ręką. Korzystaj, by się zainspirować, lepiej wszystko zaplanować, a na miejscu dotrzeć szybko do celu dzięki prostej nawigacji.',
    color: '#FFD93D',
    image: '/static/images/teneryfa/feature-page-1.jpg',
  },
  {
    id: 2,
    emoji: '📅',
    title: 'Gotowe Plany Podróży',
    description:
      'Każda chwila spędzona na szukaniu najlepszych miejsc i ustalanie tras na kolejny dzień, to czas, który możesz poświęcić na cieszenie się wakacjami. Skorzystaj z gotowca na 4, 7 lub 10 dni i odpocznij od planowania!',
    color: '#FF9F1C',
    image: '/static/images/teneryfa/feature-page-2.jpg',
  },
  {
    id: 3,
    emoji: '🥾',
    title: 'Trasy Trekkingowe',
    description:
      'Wybierz szlak w pobliżu, wiedząc, ile potrzebujesz na niego czasu i siły. Trasy, które przygotowałam dla Ciebie w interaktywnych Mapach.cz poprowadzą Cię, gdziekolwiek się znajdziesz, również offline.',
    color: '#8BC34A',
    image: '/static/images/teneryfa/feature-page-3.jpg',
  },
  {
    id: 4,
    emoji: '🍽️',
    title: 'Lista Polecanych Restauracji',
    description:
      'Wakacje są zbyt krótkie na niesprawdzone restauracje. Skorzystaj z podpowiedzi i udaj się do miejsc, w których jedzenie Cię nie rozczaruje. Uniknij turystycznych pułapek i napompowanych cen. Jesz wege? Znajdziesz tu całą listę miejsc odpowiednich dla Ciebie.',
    color: '#F67280',
    image: '/static/images/teneryfa/feature-page-4.jpg',
  },
  {
    id: 5,
    emoji: '🛠️',
    title: 'Pomoc Organizacyjna',
    description:
      'Przewodnik napisałam tak, aby wszystko, co jest potrzebne było pod ręką – ceny wstępów, parkingi i restauracje, które znajdziesz w pobliżu. Oznaczyłam to, czego należy się spodziewać, by uniknąć nieprzyjemnych niespodzianek. Ponadto znajdziesz tu mnóstwo linków, które ułatwią Ci szybkie przejście na interesującą stronę lub ustalenie trasy do danej atrakcji.',
    color: '#F27C7B',
    image: '/static/images/teneryfa/feature-page-5.jpg',
  },
  {
    id: 6,
    emoji: '💡',
    title: 'Ciekawostki',
    description:
      'Poznaj Teneryfę z innej strony. Przeczytaj o lokalnych zwyczajach, poznaj ciekawostki o miejscach, które będziesz odwiedzać. Zabierz przewodnik ze sobą, by mógł Ci służyć podczas całej podróży!',
    color: '#A29BFE',
    image: '/static/images/teneryfa/feature-page-6.jpg',
  },
];

interface FeatureItemProps {
  children?: ReactNode;
  'data-active'?: boolean;
  isLast?: boolean;
  onClick?: () => void;
}

const ForwardedFeatureItem = forwardRef<HTMLDivElement, FeatureItemProps>(
  (props, ref) => <FeatureItem {...props} ref={ref} />,
);

ForwardedFeatureItem.displayName = 'ForwardedFeatureItem';

export const Features = (): ReactElement => {
  const [activeFeature, setActiveFeature] = useState(1);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const featureRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [top, setTop] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const height = imageWrapperRef.current?.clientHeight;
      const windowHeight = window.innerHeight;
      const top = (windowHeight - height) / 2;
      setTop(top);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleFeatureClick = (index: number) => {
    const featureElement = featureRefs.current[index];
    if (featureElement) {
      featureElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <SectionWrap>
      <Title>Nie tylko książka, a&nbsp;kompan podróży</Title>
      <FeaturesWrapper>
        <MobileLayout>
          {features.map(feature => (
            <FeatureItem key={`featureMobile-${feature.id}`}>
              <ImageColumn>
                <Image src={feature.image} alt={feature.title} fill />
              </ImageColumn>
              <ContentColumn>
                <FeatureTitle>
                  {feature.emoji} {feature.title}
                </FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </ContentColumn>
            </FeatureItem>
          ))}
        </MobileLayout>

        <DesktopLayout>
          <ContentColumn>
            <FeatureList>
              {features.map((feature, index) => (
                <InView
                  key={`featureItem-${feature.id}`}
                  rootMargin="-50% 0px -50% 0px"
                  threshold={0}
                  onChange={inView => {
                    if (inView) {
                      setActiveFeature(feature.id);
                    }
                  }}
                >
                  <ForwardedFeatureItem
                    ref={el => (featureRefs.current[index] = el)}
                    data-active={activeFeature === feature.id}
                    isLast={feature.id === features.length}
                    onClick={() => handleFeatureClick(index)}
                  >
                    <FeatureTitle>
                      {feature.emoji} {feature.title}
                    </FeatureTitle>
                    <FeatureDescription>
                      {feature.description}
                    </FeatureDescription>
                  </ForwardedFeatureItem>
                </InView>
              ))}
            </FeatureList>
          </ContentColumn>
          <ImageColumn>
            <ImageWrapper ref={imageWrapperRef} style={{ top: top + 'px' }}>
              {features.map(feature => (
                <Image
                  key={`featurePage-${feature.id}`}
                  src={feature.image}
                  alt={feature.title}
                  style={{ opacity: activeFeature === feature.id ? 1 : 0 }}
                  fill
                />
              ))}
            </ImageWrapper>
          </ImageColumn>
        </DesktopLayout>
      </FeaturesWrapper>
    </SectionWrap>
  );
};
